import {
  DocumentationDeleteMenu,
  DocumentationLegendCounter,
  DocumentationLegendGlyph,
  DocumentationLegendRow,
} from './index';
import type { DocumentationLegendEntryProps } from './types';

/**
 * One legend entry (Figma `tickmark-input` 36269:159547): the mark glyph, the meaning,
 * the count pill and a menu with one action. The PDF editor and Data analysis both
 * draw their legends with it, so an entry looks and behaves the same in both (P-22,
 * PAT-18).
 *
 * - **Row**: a click focuses the entry (`onToggleFocus`). The focused entry wears the
 *   teal band.
 * - **Glyph**: a button that re-types the mark when `onGlyphClick` is given, otherwise
 *   static.
 * - **Body**: the meaning field. Its menu and counter hide while it is being typed in,
 *   because the editor's formatting toolbar opens over them.
 * - **Count**: opens the places popover on hover and pins it on click when the
 *   handlers are given, otherwise a plain label. No count, no pill.
 * - **Menu**: one action. Its tooltip says what it does; a refused action stays in
 *   the menu, disabled, with its reason as the tooltip instead. A frozen entry shows
 *   `readOnlyHint` there and on the glyph.
 */
export function DocumentationLegendEntry({
  id,
  glyph,
  glyphLabel,
  onGlyphClick,
  body,
  count,
  countLabel,
  onCountClick,
  onCountHover,
  onCountLeave,
  focused = false,
  onToggleFocus,
  action,
  menuLabel,
  readOnly = false,
  readOnlyHint,
}: DocumentationLegendEntryProps) {
  const actionRefusal = readOnly ? readOnlyHint : action.blockedReason;

  return (
    <DocumentationLegendRow
      data-testid={`legend-row-${id}`}
      highlighted={focused}
      hideActionsWhileEditing
      onClick={onToggleFocus}
      leading={
        <DocumentationLegendGlyph
          disabled={readOnly}
          onClick={
            onGlyphClick
              ? (event) => {
                  event.stopPropagation();
                  onGlyphClick(event.currentTarget);
                }
              : undefined
          }
          // The label names the button's action; a static glyph is part of the row's text.
          ariaLabel={onGlyphClick ? glyphLabel : undefined}
          title={onGlyphClick ? readOnlyHint : undefined}
          data-testid={`legend-glyph-${id}`}
        >
          {glyph}
        </DocumentationLegendGlyph>
      }
      actions={
        <>
          {count !== undefined && (
            <DocumentationLegendCounter
              active={focused}
              onClick={
                onCountClick
                  ? (event) => {
                      event.stopPropagation();
                      onCountClick(event.currentTarget);
                    }
                  : undefined
              }
              onMouseEnter={onCountHover ? (event) => onCountHover(event.currentTarget) : undefined}
              onMouseLeave={onCountLeave}
              ariaLabel={countLabel}
              data-testid={`legend-counter-${id}`}
            >
              {count}
            </DocumentationLegendCounter>
          )}
          {/* Disabled at the item, not the trigger: the menu still opens, which is where the reason is. */}
          <DocumentationDeleteMenu
            onDelete={action.onSelect}
            deleteDisabled={readOnly || action.blockedReason !== undefined}
            deleteTooltip={actionRefusal ?? action.description}
            deleteLabel={action.label}
            deleteIcon={action.icon}
            triggerLabel={menuLabel}
            data-testid={`legend-menu-${id}`}
          />
        </>
      }
    >
      {body}
    </DocumentationLegendRow>
  );
}
